import * as stylex from '@stylexjs/stylex';
import type { UseQueryOptions } from '@tanstack/react-query';
import {
  CatchBoundary,
  createFileRoute,
  Outlet,
  useMatches,
  useRouter,
} from '@tanstack/react-router';
import { Fragment } from 'react';
import {
  array as vArray,
  boolean as vBoolean,
  fallback as vFallback,
  nullish as vNullish,
  number as vNumber,
  object as vObject,
  string as vString,
  unknown as vUnknown,
} from 'valibot';

import { PendingState } from '@/components/pending-state.component';
import { Heading, TABLE_TOOLBAR_SLOTS, TableToolbar, View } from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { $ } from '@/libs/serenity-ui/tokens.stylex';

const DEFAULT_SORTING = { id: 'createdAt', desc: true };
const DEFAULT_PAGING = { pageIndex: 0, pageSize: 100 };

export interface ListRouteContext {
  resource: string;
  can: Record<string, Record<string, (...args: unknown[]) => boolean>>;
  queryOptions: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    list: UseQueryOptions<any, any, { totalCount: number; [key: string]: any; }, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const Route = createFileRoute('/_main/_list')({
  component: RouteComponent,
  validateSearch: vObject({
    pagination: vFallback(vNullish(vObject({ pageIndex: vNumber(), pageSize: vNumber() }), DEFAULT_PAGING), DEFAULT_PAGING),
    columnFilters: vFallback(vNullish(vArray(vObject({ id: vString(), value: vUnknown() })), []), []),
    sorting: vFallback(vNullish(vObject({ id: vString(), desc: vBoolean() }), DEFAULT_SORTING), DEFAULT_SORTING),
  }),
  pendingComponent: PendingState,
});

function RouteComponent() {
  const router = useRouter();
  const matches = useMatches();

  const childMatch = matches.at(-1);
  const staticData = childMatch?.staticData?.list;

  const isOutlined = router.history.location.hash === '#table';

  const Provider = staticData?.slots?.providerComponent ?? Fragment;

  return (
    <Provider>
      <View style={[ styles.base, isOutlined && styles.outlined ]}>
        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
          {staticData?.slots?.topBarComponent &&
            <View style={topBarViewStyles.base}>
              <staticData.slots.topBarComponent />
            </View>}
        </CatchBoundary>

        <TableToolbar style={!!staticData?.slots?.topBarComponent && tableToolbarStyles.noTopPadding}>
          <View slot={TABLE_TOOLBAR_SLOTS.LEFT_VIEW}>
            {staticData?.heading && <Heading>{staticData?.heading}</Heading>}
            <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
              {staticData?.slots?.toolbarLeftComponent && <staticData.slots.toolbarLeftComponent />}
            </CatchBoundary>
          </View>

          <View slot={TABLE_TOOLBAR_SLOTS.RIGHT_VIEW}>
            <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
              {staticData?.slots?.toolbarActionsComponent && <staticData.slots.toolbarActionsComponent />}
            </CatchBoundary>
          </View>
        </TableToolbar>

        <View style={contentViewStyles.base}>
          <Outlet />
        </View>

        <CatchBoundary errorComponent={() => null} getResetKey={() => 'reset'} onCatch={(error, errorInfo) => console.error(error, errorInfo)}>
          {staticData?.slots?.bottomBarComponent && <staticData.slots.bottomBarComponent />}
        </CatchBoundary>
      </View>
    </Provider>
  );
}

const styles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    height: {
      default: '100%',
      [breakpoints.large]: '100vh',
    },
    overflow: 'hidden',
    width: '100%',
  },
  outlined: {
    borderColor: $.accentChilli60,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
  },
});

const topBarViewStyles = stylex.create({
  base: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    backgroundColor: $.surfaceBGPrimary,
    display: 'flex',
    flexDirection: 'column',
    height: 48,
    justifyContent: 'center',
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: 8,
  },
});

const tableToolbarStyles = stylex.create({
  noTopPadding: {
    paddingTop: 0,
  },
});

const contentViewStyles = stylex.create({
  base: {
    contain: 'layout style paint', // Isolate from external layout changes
    contentVisibility: 'auto',
    flex: '1',
    minHeight: 0,
    width: '100%',
  },
});
